import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sign_in, getUserProfileService, alert_chat_initialize } from '../services';
import ProfileCompletionModal from '../Components/ProfileCompletionModal';
import TermsService from '../Components/TermsServices';
import PrivacyPolicy from '../Components/PrivacyPolicy';
import SsoLoginButton from './ssoAuth/SsoLoginButton';
import useSsoDiscovery from './ssoAuth/useSsoDiscovery';
import LoginOtpStep from './LoginOtpStep';
import { isOrgAdminOnly } from '../helper';
import AuthLayout, { trackPointer, Field, AlertCircleIcon, CheckCircleIcon, ArrowRightIcon, MailIcon, LockIcon, EyeButton } from './AuthLayout';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_PATTERN = /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/;

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [resMsg, setResMsg] = useState('');
  const [errResMsg, setErrResMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [termsServiceModal, setTermsServiceModal] = useState(false);
  const [privacyPolicyModal, setPrivacyPolicyModal] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { sso, isChecking: isCheckingSso } = useSsoDiscovery(email);
  const ssoOnly = sso.ssoEnabled && sso.enforceSso;
  const busy = isLoading || isCheckingSso;

  useEffect(() => {
    sessionStorage.setItem('otp_source_page', location.pathname);
  }, [location.pathname]);

  const validate = () => {
    const emailError = !email
      ? 'Email is required.'
      : !EMAIL_PATTERN.test(email)
      ? 'Enter a valid email.'
      : '';
    const passwordError = !password
      ? 'Password is required.'
      : !PASSWORD_PATTERN.test(password)
      ? 'Password must be 8+ characters with uppercase, lowercase, number and special character.'
      : '';
    setErrors({ email: emailError, password: passwordError });
    return !emailError && !passwordError;
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    if (name === 'email') { setEmail(value); setErrResMsg(''); }
    else if (name === 'password') { setPassword(value); setErrResMsg(''); }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const isProfileIncomplete = (profile) => {
    if (!profile) return true;
    return ['industry_group', 'region', 'primary_regulatory_profile', 'customer_size']
      .some(f => !profile?.organization[f] || profile?.organization[f] === '');
  };

  const completeLogin = async (data) => {
    sessionStorage.setItem('access_token', data?.access_token);
    sessionStorage.setItem('organization_id', data?.organization_id);
    sessionStorage.setItem('refresh_token', data?.refresh_token);
    localStorage.setItem('organization_id', data?.organization_id);
    sessionStorage.setItem('is_otp_allowed', '/');
    localStorage.setItem('email', email);
    if (data?.risk_role) localStorage.setItem('risk_role', data.risk_role);
    else localStorage.removeItem('risk_role');
    if (data?.flags) sessionStorage.setItem('user_flags', JSON.stringify(data.flags));

    try { await alert_chat_initialize({ organization_id: data?.organization_id }); } catch {}

    try {
      const profileRes = await getUserProfileService();
      const fetchedProfile = profileRes?.statusCode?.response_data?.data;
      if (fetchedProfile) {
        localStorage.setItem('user_profile', JSON.stringify(fetchedProfile));
        setProfileData(fetchedProfile);
        if (isOrgAdminOnly() && isProfileIncomplete(fetchedProfile)) {
          setShowProfileModal(true);
          return;
        }
      }
    } catch {}

    const savedRedirect = sessionStorage.getItem('redirect_after_login');
    sessionStorage.removeItem('redirect_after_login');
    navigate(savedRedirect || '/product-dashboard');
  };

  const restartLogin = () => {
    setOtpChallenge(null);
    setResMsg('');
    setErrResMsg('');
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (ssoOnly || isCheckingSso || isLoading) return;
    setIsLoading(true);
    if (!validate()) { setIsLoading(false); return; }

    try {
      const response = await sign_in({ emailId: email, password });
      setIsLoading(false);

      const successMessage = response?.statusCode?.response_data?.message;
      const errorMessage = response?.errors?.message;
      const errMsg = response?.error?.message;

      if (successMessage) {
        setErrResMsg('');
        const data = response?.statusCode?.response_data?.data;
        if (data?.otp_required) {
          setResMsg('');
          setPassword('');
          setOtpChallenge(data);
          return;
        }
        setResMsg(successMessage);
        await completeLogin(data);
      } else if (errMsg) {
        setErrResMsg(errMsg);
        setResMsg('');
        setIsLoading(false);
      } else if (errorMessage) {
        setErrResMsg(errorMessage);
        setIsLoading(false);
      }
    } catch {
      setIsLoading(false);
      setErrResMsg('Something went wrong. Please try again.');
    }
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    const redirectTo = sessionStorage.getItem('redirect_after_login') || '/product-dashboard';
    sessionStorage.removeItem('redirect_after_login');
    navigate(redirectTo);
  };

  return (
    <>
      <AuthLayout>
        <AuthLayout.Stage/>
        <AuthLayout.Panel>
        {otpChallenge ? (
          <LoginOtpStep challenge={otpChallenge} onVerified={completeLogin} onRestart={restartLogin}/>
        ) : (
        <div className="lg-wrap">
          <header className="lg-form-header">
            <p className="lg-eyebrow"><LockIcon/> Secure sign in</p>
            <h1 className="lg-title" id="lg-title">Welcome back</h1>
            <p className="lg-lead">Sign in to see what could cost you money, and what to fix first.</p>
          </header>

          {errResMsg && (
            <div className="lg-alert lg-alert--error" role="alert">
              <AlertCircleIcon/> <span>{errResMsg}</span>
            </div>
          )}
          {resMsg && (
            <div className="lg-alert lg-alert--success" role="status">
              <CheckCircleIcon/> <span>{resMsg}</span>
            </div>
          )}

          <form className="lg-form" noValidate onSubmit={submitHandler}>
            <Field name="email" type="email" label="Work email" icon={<MailIcon/>}
              value={email} onChange={onChangeHandler} error={errors.email}
              readOnly={isLoading} autoComplete="username"/>

            {!sso.ssoEnabled && (
              <>
                <Field name="password" type={showPass ? 'text' : 'password'} label="Password" icon={<LockIcon/>}
                  value={password} onChange={onChangeHandler} error={errors.password}
                  readOnly={isLoading} autoComplete="current-password"
                  trailing={<EyeButton visible={showPass} onToggle={() => setShowPass(s => !s)}/>}
                />

                <div className="lg-row">
                  <span/>
                  <button type="button" className="lg-link" onClick={() => navigate('/forget-password')}>
                    Forgot password?
                  </button>
                </div>

                {!ssoOnly && (
                  <button type="submit" className="lg-submit lg-spot"
                    disabled={busy} aria-busy={isLoading} onPointerMove={trackPointer}>
                    {isLoading ? (
                      <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Verifying...</span>
                    ) : isCheckingSso ? (
                      <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Checking...</span>
                    ) : (
                      <span className="lg-submit-label">Sign in <ArrowRightIcon/></span>
                    )}
                    {isLoading && <i className="lg-scan" aria-hidden="true"/>}
                  </button>
                )}
              </>
            )}
          </form>

          <SsoLoginButton sso={sso} isChecking={isCheckingSso} disabled={isLoading}/>

          <p className="lg-signup">
            Don't have an account?{' '}
            <button type="button" className="lg-link" onClick={() => navigate('/sign-up')}>Sign up</button>
          </p>
        </div>
        )}

        <p className="lg-trust">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
            <path d="M8 10.5V8a4 4 0 018 0v2.5"/>
          </svg>
          Protected by enterprise-grade security. By continuing you agree to our{' '}
          <button type="button" className="lg-link" onClick={() => setTermsServiceModal(true)}>Terms of Service</button>
          {' '}and{' '}
          <button type="button" className="lg-link" onClick={() => setPrivacyPolicyModal(true)}>Privacy Policy</button>.
        </p>
        </AuthLayout.Panel>
      </AuthLayout>

      <ProfileCompletionModal
        show={showProfileModal}
        onHide={() => {}}
        onSuccess={handleProfileComplete}
        existingProfile={profileData}
      />
      {termsServiceModal && <TermsService show={termsServiceModal} onHide={() => setTermsServiceModal(false)}/>}
      {privacyPolicyModal && <PrivacyPolicy show={privacyPolicyModal} onHide={() => setPrivacyPolicyModal(false)}/>}
    </>
  );
}

export default SignIn;
